import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Mail, 
  Lock, 
  ArrowRight, 
  Eye, 
  EyeOff,
  Sparkles,
  Shield,
  Zap,
  Users
} from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      
      console.log('Connexion réussie:', data);
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Erreur connexion:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Demo credentials
  const demoUsers = [
    { role: 'Super Admin', email: 'superadmin@demo.com', password: 'password123' },
    { role: 'Admin', email: 'admin@demo.com', password: 'password123' },
    { role: 'Manager', email: 'manager@demo.com', password: 'password123' },
    { role: 'Agent', email: 'agent@demo.com', password: 'password123' }
  ];

  const fillDemoCredentials = (demoEmail, demoPassword) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex">
      {/* Left Panel - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-3 rounded-2xl shadow-lg shadow-blue-500/25">
              <BarChart3 size={32} className="text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Insight360</span>
          </div>

          <div className="space-y-8">
            <h1 className="text-4xl font-bold text-white leading-tight">
              Gérez votre activité<br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                en toute simplicité
              </span>
            </h1>
            
            <p className="text-gray-300 text-lg">
              La solution CRM complète pour les équipes modernes. 
              Centralisez vos leads, automatisez vos processus et 
              augmentez vos ventes.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-300">
                <div className="bg-green-500/20 p-1.5 rounded-lg">
                  <Zap size={18} className="text-green-400" />
                </div>
                <span>Automatisation intelligente des tâches</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <div className="bg-blue-500/20 p-1.5 rounded-lg">
                  <Users size={18} className="text-blue-400" />
                </div>
                <span>Gestion d'équipe en temps réel</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <div className="bg-purple-500/20 p-1.5 rounded-lg">
                  <Shield size={18} className="text-purple-400" />
                </div>
                <span>Sécurité et conformité RGPD</span>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="relative z-10 bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex -space-x-2">
              {[1,2,3,4].map(i => (
                <img
                  key={i}
                  src={`https://randomuser.me/api/portraits/${i % 2 ? 'women' : 'men'}/${i + 30}.jpg`}
                  alt="User"
                  className="w-8 h-8 rounded-full border-2 border-gray-800"
                />
              ))}
            </div>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => (
                <Sparkles key={i} size={14} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
          </div>
          <p className="text-gray-300 text-sm">
            "Insight360 a transformé notre façon de travailler. 
            Une interface intuitive et des fonctionnalités puissantes."
          </p>
          <p className="text-gray-400 text-xs mt-2">— Sophie Martin, Directrice Commerciale</p>
        </div>

        <div className="relative z-10 text-gray-400 text-sm">
          © 2025 Insight360. Tous droits réservés.
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="max-w-md w-full space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-2.5 rounded-xl">
              <BarChart3 size={24} className="text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Insight360</span>
          </div>

          {/* Header */}
          <div className="text-center lg:text-left">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Content de vous revoir
            </h2>
            <p className="text-gray-600">
              Connectez-vous pour accéder à votre espace
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                Email professionnel
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-gray-50/50"
                  placeholder="vous@entreprise.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-gray-50/50"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">Se souvenir de moi</span>
              </label>
              <a href="#" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                Mot de passe oublié ?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white py-3.5 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Se connecter
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="pt-6">
            <p className="text-xs text-gray-500 text-center mb-4">
              Comptes de démonstration
            </p>
            <div className="grid grid-cols-2 gap-2">
              {demoUsers.map((demo, index) => (
                <button
                  key={index}
                  onClick={() => fillDemoCredentials(demo.email, demo.password)}
                  className="text-xs bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-2 transition-colors text-left"
                >
                  <span className="font-medium text-gray-700 block">{demo.role}</span>
                  <span className="text-gray-500 text-[10px]">{demo.email}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sign up link */}
          <p className="text-center text-sm text-gray-600 pt-4">
            Vous n'avez pas de compte ?{' '}
            <a href="#" className="text-blue-600 hover:text-blue-700 font-medium">
              Contacter l'administrateur
            </a>
          </p>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
